import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import IFireStation, { IFireStationCreateOrUpdate, IFireStationInitResult } from "models/sql/IFireStation";
import fireStationsService from "services/FireStationService";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import FireDepartmentService from "services/FireDepartmentService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { generateGoogleMapsDirectionsLinkUrl } from "utilities/BrowserUtility";
import { generateLog } from "utilities/MiscUtilities";
import CountryChooser from "components/CountryChooser";
import MarkdownEditor from "components/MarkdownEditor";
import StateChooser from "components/StateChooser";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";

interface IFireStationsDetailProps {
    baseData?: IFireStationInitResult;
}

const FireStationsDetail = (props: IFireStationsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [searchParams] = useSearchParams();
    const defs = searchParams.get("defs") || "{}";
    const parsedDefaults = defs ? JSON.parse(defs) : undefined;
    const [fireStation, setfireStation] = useState<IFireStation | null>({
        ... {
            fireStationId: -1,
            address1: "",
            address2: "",
            airDecon: "",
            cancerCommittee: false,
            city: "",
            contactEmail: "",
            contactName: "",
            contactPhone: "",
            country: "",
            dealerId: undefined,
            dedicatedBunkerRoom: false,
            emsvehicles: 0,
            fax: "",
            largeCeilingFansInBay: false,
            largeHeatingInBay: false,
            name: "",
            number: "",
            otherApparatus: 0,
            phone: "",
            postalCode: "",
            ppestorage: "",
            safetyCommittee: false,
            sleepingQuarters: false,
            state: "",
            type1Apparatus: 0,
            fireDepartmentId: undefined,
            vehicleType: "",
            notes: "",
            yearBuilt: (new Date()).getFullYear(),
            latitude: undefined,
            longitude: undefined,
            systemDataJson: ""
        }, ...(parsedDefaults ?? {})
    });
    const [department, setDepartment] = useState<IFireDepartment>();
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await fireStationsService.GetAsync(accessToken, id);

                    if (result.record) {
                        setfireStation(result.record);

                        if (result.record.fireDepartmentId != null && result.record.fireDepartmentId != undefined) {
                            const departmentResult = await FireDepartmentService.GetAsync(accessToken, result.record.fireDepartmentId);

                            if (departmentResult.record) {
                                setDepartment(departmentResult.record);
                            }
                        }
                    }
                    else {
                        setfireStation(null);
                        globalContext.showErrorMessage("Unable to load Fire Department");
                    }

                    globalContext.setLoading(false);
                }
                else {
                    if (fireStation?.fireDepartmentId != null && fireStation?.fireDepartmentId != undefined) {
                        globalContext.setLoading(true);
                        const accessToken = await globalContext.getAccessTokenAsync();
                        const departmentResult = await FireDepartmentService.GetAsync(accessToken, fireStation.fireDepartmentId);

                        if (departmentResult.record) {
                            setDepartment(departmentResult.record);
                        }

                        globalContext.setLoading(false);
                    }
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (fireStation && fireStation.fireStationId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await fireStationsService.DeleteAsync(accessToken, fireStation.fireStationId);
            globalContext.setLoading(false);
            navigate("/Departments");
        }
    };

    const submitFormAsync = async () => {
        if (fireStation) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IFireStationCreateOrUpdate = {
                fireStationId: fireStation.fireStationId,
                fireDepartmentId: fireStation.fireDepartmentId,
                dealerId: fireStation.dealerId,
                address1: fireStation.address1,
                address2: fireStation.address2,
                city: fireStation.city,
                state: fireStation.state,
                postalCode: fireStation.postalCode,
                type1Apparatus: fireStation.type1Apparatus,
                otherApparatus: fireStation.otherApparatus,
                sleepingQuarters: fireStation.sleepingQuarters,
                dedicatedBunkerRoom: fireStation.dedicatedBunkerRoom,
                cancerCommittee: fireStation.cancerCommittee,
                safetyCommittee: fireStation.safetyCommittee,
                emsvehicles: fireStation.emsvehicles,
                largeCeilingFansInBay: fireStation.largeCeilingFansInBay,
                largeHeatingInBay: fireStation.largeHeatingInBay,
                name: fireStation.name,
                number: fireStation.number,
                contactName: fireStation.contactName,
                contactEmail: fireStation.contactEmail,
                contactPhone: fireStation.contactPhone,
                country: fireStation.country,
                phone: fireStation.phone,
                fax: fireStation.fax,
                yearBuilt: fireStation.yearBuilt,
                ppestorage: fireStation.ppestorage,
                airDecon: fireStation.airDecon,
                vehicleType: fireStation.vehicleType,
                notes: fireStation.notes,
                longitude: undefined,
                latitude: undefined,
                systemDataJson: fireStation.systemDataJson
            };

            await fireStationsService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.fireStationId === -1) {
                navigate(-1);
            }
        }
    };

    let debounceId = 0;
    const [departmentNames, setDepartmentNames] = useState<string[]>();
    const [departmentFilter, setDepartmentFilter] = useState<IFireDepartmentFilters>();
    const [fireDepartments, setFireDepartments] = useState<IFireDepartment[]>([]);
    const [showDepartmentChooser, setShowDepartmentChooser] = useState(false);

    useEffect(
        () => {
            (async () => {
                if (departmentFilter) {
                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await FireDepartmentService.GetAllAsync(accessToken, 0, 20, departmentFilter);

                    if (result.records != null) {
                        setFireDepartments(result.records);
                    }
                    else {
                        globalContextRef.current.showErrorMessage("Unable to load Fire Departments");
                    }
                }
            })();
        },
        [departmentFilter]);

    const showSelectableDepartment = (fireStation: IFireStation) => (
        <Row className="align-items-end">
            <Col className="dynamiceObjectSelectorButton">
                {
                    !showDepartmentChooser &&
                    <Form.Group
                        className="p-1"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            if (fireStation.postalCode) {
                                setDepartmentNames([]);
                                setDepartmentFilter({});
                            }

                            setShowDepartmentChooser(!showDepartmentChooser);
                        }} >
                        <Form.Label>
                            Department
                            {
                                fireStation.fireDepartmentId &&
                                <>
                                    &nbsp;
                                    <FontAwesomeIcon
                                        icon={faUpRightFromSquare as IconProp}
                                        style={{ cursor: "pointer" }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            window.open(`/DPS/Departments/Detail/${fireStation.fireDepartmentId}`)
                                        }} />
                                </>
                            }
                        </Form.Label>
                        <h2>{department?.fireDeptName || "None Selected"}</h2>
                    </Form.Group>
                }

                <div>
                    {
                        showDepartmentChooser &&
                        <>
                            <h3>Choose a Department</h3>
                            <Row className="align-items-end">
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>Department Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            autoComplete="none"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setDepartmentNames(e.target.value.split(","));
                                                    window.clearTimeout(debounceId);
                                                    debounceId = window.setTimeout(() => {
                                                        setDepartmentFilter({ ...departmentFilter, fireDeptNames: departmentNames });
                                                    }, 500);
                                                }
                                            }
                                            value={departmentNames?.join(",") || ""}
                                        /><br />
                                        <strong>Click a Department to select.</strong>
                                        <Table striped bordered hover size="sm" className="m-1">
                                            <tbody>
                                                {
                                                    !fireDepartments || fireDepartments.length < 1 &&
                                                    <tr><td>No matches.</td></tr>
                                                }
                                                {
                                                    fireDepartments.map(
                                                        entry => <tr
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => {
                                                                setfireStation({ ...fireStation, fireDepartmentId: entry.fireDepartmentId });
                                                                setDepartment(entry);
                                                                setShowDepartmentChooser(false);
                                                            }}><td>{entry.fireDeptName} {entry.hqaddr1} {entry.hqcity} {entry.hqstate}, {entry.hqzip} {entry.hqCountry}</td></tr>
                                                    )
                                                }

                                            </tbody>
                                        </Table><br />
                                        <Button
                                            className="mb-1"
                                            style={{ width: "100%" }}
                                            onClick={
                                                () => setShowDepartmentChooser(false)}>Cancel Department Update</Button>

                                        <Button
                                            style={{ width: "100%" }}
                                            onClick={
                                                () => {
                                                    setfireStation({ ...fireStation, fireDepartmentId: undefined });
                                                    setDepartment(undefined);
                                                    setShowDepartmentChooser(false);
                                                }}>Set to None</Button>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    }
                </div>
            </Col>
        </Row>
    );

    if (fireStation) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <strong>{fireStation.name} ({fireStation.number})&nbsp;
                                            <FontAwesomeIcon
                                                icon={faUpRightFromSquare as IconProp}
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    window.open(generateGoogleMapsDirectionsLinkUrl(`${fireStation.address1} ${fireStation.city} ${fireStation.state}, ${fireStation.postalCode}`))
                                                }} /></strong>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Fire Station</strong>
                                    }
                                </Card.Title>

                                {showSelectableDepartment(fireStation)}

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, name: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.name}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Number</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, number: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.number}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Year Built</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, yearBuilt: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.yearBuilt?.toString() || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Contact Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, contactName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.contactName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Contact Phone</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, contactPhone: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.contactPhone}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Contact Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, contactEmail: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.contactEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>


                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Address 1</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, address1: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.address1}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Address 2</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, address2: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.address2}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>City</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, city: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.city}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>State</Form.Label>
                                            {
                                                <StateChooser
                                                    onChange={newVal => {
                                                        setfireStation({ ...fireStation, state: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    defaultValue={fireStation.state}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Postal Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, postalCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.postalCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Country</Form.Label>
                                            <CountryChooser
                                                onChange={newVal => {
                                                    setfireStation({ ...fireStation, country: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={fireStation.country}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Phone</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, phone: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.phone}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Fax</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, fax: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.fax}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Type 1 Apparatus</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, type1Apparatus: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.type1Apparatus || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Other Apparatus</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, otherApparatus: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.otherApparatus || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>EMS Vehicles</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, emsvehicles: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.emsvehicles || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Sleeping Quarters</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            setfireStation({ ...fireStation, sleepingQuarters: e.target.value === "true" });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={(fireStation?.sleepingQuarters || false).toString()}>
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Dedicated Bunker Room</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            setfireStation({ ...fireStation, dedicatedBunkerRoom: e.target.value === "true" });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={(fireStation?.dedicatedBunkerRoom || false).toString()}>
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Cancer Committee</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            setfireStation({ ...fireStation, cancerCommittee: e.target.value === "true" });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={(fireStation?.cancerCommittee || false).toString()}>
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Safety Committee</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            setfireStation({ ...fireStation, safetyCommittee: e.target.value === "true" });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={(fireStation?.safetyCommittee || false).toString()}>
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>PPE Storage</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, ppestorage: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.ppestorage}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Large Ceiling Fans In Bay</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            setfireStation({ ...fireStation, largeCeilingFansInBay: e.target.value === "true" });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={(fireStation?.largeCeilingFansInBay || false).toString()}>
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Large Heating In Bay</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            setfireStation({ ...fireStation, largeHeatingInBay: e.target.value === "true" });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={(fireStation?.largeHeatingInBay || false).toString()}>
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Air Decon</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            setfireStation({ ...fireStation, airDecon: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.airDecon}>
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Vehicle Type</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLSelectElement>) => {
                                                            setfireStation({ ...fireStation, vehicleType: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.vehicleType}>
                                                    <option value="true">True</option>
                                                    <option value="false">False</option>
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Latitude</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, latitude: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.latitude}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Longitude</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setfireStation({ ...fireStation, longitude: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={fireStation.longitude}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                globalContextRef={globalContextRef.current}
                                                noun="Note"
                                                onChange={newVal => {
                                                    setfireStation({ ...fireStation, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={fireStation.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <Row className="align-items-end">
                        <Col>
                            <Card className="mb-2">
                                <Card.Body>
                                    <Card.Title>
                                        <strong>Log</strong><br />
                                    </Card.Title>
                                    {generateLog<IFireStation>(fireStation)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                }

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Fire Station, please try again.</h1>
            </>
        );
    }
}

export default FireStationsDetail;
